import { useState, useMemo, ReactNode } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronUp, ChevronsUpDown, MoreHorizontal, Search, Settings2, Circle, LayoutGrid, List } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DataTableColumn<T> {
  id: string;
  header: string;
  accessorKey?: keyof T | string;
  accessorFn?: (row: T) => any;
  cell?: (row: T) => ReactNode;
  cardCell?: (row: T) => ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  filterOptions?: { label: string; value: string }[];
  defaultVisible?: boolean;
  showInCard?: boolean;
  width?: string;
  className?: string;
}

export interface DataTableFilter {
  id: string;
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}

export interface DataTableAction<T> {
  label: string;
  icon?: ReactNode;
  onClick: (row: T) => void;
  variant?: 'default' | 'destructive';
  show?: (row: T) => boolean;
}

export interface MassAction<T> {
  label: string;
  icon?: ReactNode;
  onClick: (selectedRows: T[]) => void;
  variant?: 'default' | 'destructive';
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  keyField: keyof T;
  searchPlaceholder?: string;
  searchFields?: (keyof T | string)[];
  filters?: DataTableFilter[];
  actions?: DataTableAction<T>[];
  massActions?: MassAction<T>[];
  onRowClick?: (row: T) => void;
  titleField?: keyof T | string;
  subtitleField?: keyof T | string;
  unreadField?: keyof T | string;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  loading?: boolean;
  className?: string;
  enableViewToggle?: boolean;
  defaultView?: 'list' | 'card';
  cardRender?: (row: T, actions: DataTableAction<T>[]) => ReactNode;
}

type SortDirection = 'asc' | 'desc' | null;

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  keyField,
  searchPlaceholder = 'Search...',
  searchFields = [],
  filters = [],
  actions = [],
  massActions = [],
  onRowClick,
  titleField,
  subtitleField,
  unreadField,
  emptyMessage = 'No data found',
  emptyIcon,
  loading = false,
  className,
  enableViewToggle = true,
  defaultView = 'list',
  cardRender,
}: DataTableProps<T>) {
  const [search, setSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'list' | 'card'>(defaultView);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(columns.filter(c => c.defaultVisible !== false).map(c => c.id))
  );

  // Get value from row using accessor
  const getValue = (row: T, column: DataTableColumn<T>): any => {
    if (column.accessorFn) return column.accessorFn(row);
    if (column.accessorKey) {
      const keys = String(column.accessorKey).split('.');
      let value: any = row;
      for (const key of keys) {
        value = value?.[key];
      }
      return value;
    }
    return null;
  };

  // Filter data
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (search && searchFields.length > 0) {
      const searchLower = search.toLowerCase();
      result = result.filter(row => 
        searchFields.some(field => {
          const keys = String(field).split('.');
          let value: any = row;
          for (const key of keys) {
            value = value?.[key];
          }
          return String(value || '').toLowerCase().includes(searchLower);
        })
      );
    }

    return result;
  }, [data, search, searchFields]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredData;

    const column = columns.find(c => c.id === sortColumn);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getValue(a, column);
      const bVal = getValue(b, column);

      if (aVal === bVal) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredData, sortColumn, sortDirection, columns]);

  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === sortedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(sortedData.map(row => String(row[keyField]))));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const toggleColumn = (columnId: string) => {
    const newVisible = new Set(visibleColumns);
    if (newVisible.has(columnId)) {
      newVisible.delete(columnId);
    } else {
      newVisible.add(columnId);
    }
    setVisibleColumns(newVisible);
  };

  const getSelectedItems = (): T[] => {
    return sortedData.filter(row => selectedRows.has(String(row[keyField])));
  };

  const visibleColumnsList = columns.filter(c => visibleColumns.has(c.id));

  const getSortIcon = (columnId: string) => {
    if (sortColumn !== columnId) return <ChevronsUpDown className="h-4 w-4 text-muted-foreground/50" />;
    if (sortDirection === 'asc') return <ChevronUp className="h-4 w-4" />;
    return <ChevronDown className="h-4 w-4" />;
  };

  const isUnread = (row: T): boolean => {
    if (!unreadField) return false;
    const keys = String(unreadField).split('.');
    let value: any = row;
    for (const key of keys) {
      value = value?.[key];
    }
    return value === true || value === false ? !value : false;
  };

  // Card columns for display
  const cardColumns = columns.filter(c => c.showInCard !== false && visibleColumns.has(c.id));

  const renderCardView = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-32">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      );
    }

    if (sortedData.length === 0) {
      return (
        <Card className="p-12 text-center">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            {emptyIcon}
            <span>{emptyMessage}</span>
          </div>
        </Card>
      );
    }

    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sortedData.map(row => {
          const rowId = String(row[keyField]);
          const unread = isUnread(row);
          const titleColumn = columns.find(c => c.accessorKey === titleField || c.id === titleField);
          const titleValue = titleColumn ? getValue(row, titleColumn) : '';
          const subtitleColumn = subtitleField ? columns.find(c => c.accessorKey === subtitleField || c.id === subtitleField) : null;
          const subtitleValue = subtitleColumn ? getValue(row, subtitleColumn) : '';

          if (cardRender) {
            return <div key={rowId}>{cardRender(row, actions)}</div>;
          }

          return (
            <Card
              key={rowId}
              className={cn(
                'p-4 hover:shadow-md transition-shadow',
                onRowClick && 'cursor-pointer',
                unread && 'border-primary/50 bg-primary/5'
              )}
              onClick={() => onRowClick?.(row)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {massActions.length > 0 && (
                    <Checkbox
                      checked={selectedRows.has(rowId)}
                      onCheckedChange={() => toggleSelect(rowId)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {unread && <Circle className="h-2 w-2 fill-primary text-primary flex-shrink-0" />}
                      <h3 className="font-semibold truncate text-primary">{titleValue || '-'}</h3>
                    </div>
                    {subtitleValue && (
                      <p className="text-sm text-muted-foreground truncate mt-0.5">{subtitleValue}</p>
                    )}
                    <div className="mt-3 space-y-1.5">
                      {cardColumns.slice(0, 4).filter(c => c.id !== titleField && c.id !== subtitleField).map(column => {
                        const value = column.cardCell ? column.cardCell(row) : (column.cell ? column.cell(row) : getValue(row, column));
                        return (
                          <div key={column.id} className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground text-xs">{column.header}:</span>
                            <span className="truncate">{value ?? '-'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {actions.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {actions.filter(action => !action.show || action.show(row)).map((action, index) => (
                        <DropdownMenuItem
                          key={index}
                          onClick={(e) => { e.stopPropagation(); action.onClick(row); }}
                          className={action.variant === 'destructive' ? 'text-destructive' : ''}
                        >
                          {action.icon}
                          <span className="ml-2">{action.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Toolbar */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filters */}
          {filters.map(filter => (
            <Select key={filter.id} value={filter.value} onValueChange={filter.onChange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder={filter.label} />
              </SelectTrigger>
              <SelectContent>
                {filter.options.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}

          {/* View Toggle */}
          {enableViewToggle && (
            <div className="flex gap-1 border rounded-lg p-1">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="gap-1.5 h-7 px-2"
              >
                <List className="h-4 w-4" />
                <span className="hidden sm:inline">List</span>
              </Button>
              <Button
                variant={viewMode === 'card' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('card')}
                className="gap-1.5 h-7 px-2"
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="hidden sm:inline">Cards</span>
              </Button>
            </div>
          )}

          {/* Column Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-2" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns.map(column => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={visibleColumns.has(column.id)}
                  onCheckedChange={() => toggleColumn(column.id)}
                >
                  {column.header}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mass Actions */}
          {massActions.length > 0 && selectedRows.size > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <Badge variant="secondary">{selectedRows.size} selected</Badge>
              {massActions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant === 'destructive' ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={() => action.onClick(getSelectedItems())}
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* View: Card or List */}
      {viewMode === 'card' ? (
        renderCardView()
      ) : (
        <Card>
          <Table>
          <TableHeader>
            <TableRow>
              {massActions.length > 0 && (
                <TableHead className="w-12">
                  <Checkbox
                    checked={sortedData.length > 0 && selectedRows.size === sortedData.length}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
              )}
              {unreadField && <TableHead className="w-8" />}
              {visibleColumnsList.map(column => (
                <TableHead
                  key={column.id}
                  className={cn(column.className, column.sortable !== false && 'cursor-pointer select-none')}
                  style={{ width: column.width }}
                  onClick={() => column.sortable !== false && handleSort(column.id)}
                >
                  <div className="flex items-center gap-1">
                    {column.header}
                    {column.sortable !== false && getSortIcon(column.id)}
                  </div>
                </TableHead>
              ))}
              {actions.length > 0 && (
                <TableHead className="w-12 text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell 
                  colSpan={visibleColumnsList.length + (massActions.length > 0 ? 1 : 0) + (unreadField ? 1 : 0) + (actions.length > 0 ? 1 : 0)} 
                  className="h-32 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                </TableCell>
              </TableRow>
            ) : sortedData.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={visibleColumnsList.length + (massActions.length > 0 ? 1 : 0) + (unreadField ? 1 : 0) + (actions.length > 0 ? 1 : 0)} 
                  className="h-32 text-center"
                >
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    {emptyIcon}
                    <span>{emptyMessage}</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map(row => {
                const rowId = String(row[keyField]);
                const unread = isUnread(row);
                
                return (
                  <TableRow 
                    key={rowId}
                    className={cn(
                      onRowClick && 'cursor-pointer hover:bg-muted/50',
                      unread && 'bg-primary/5 font-medium'
                    )}
                  >
                    {massActions.length > 0 && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedRows.has(rowId)}
                          onCheckedChange={() => toggleSelect(rowId)}
                        />
                      </TableCell>
                    )}
                    {unreadField && (
                      <TableCell>
                        {unread && (
                          <Circle className="h-2 w-2 fill-primary text-primary" />
                        )}
                      </TableCell>
                    )}
                    {visibleColumnsList.map((column, colIndex) => {
                      const value = getValue(row, column);
                      const isTitleColumn = titleField && (column.accessorKey === titleField || column.id === titleField);
                      
                      return (
                        <TableCell
                          key={column.id}
                          className={cn(
                            column.className,
                            isTitleColumn && 'font-medium text-primary hover:underline cursor-pointer'
                          )}
                          onClick={() => {
                            if (isTitleColumn && onRowClick) {
                              onRowClick(row);
                            }
                          }}
                        >
                          {column.cell ? column.cell(row) : value ?? '-'}
                        </TableCell>
                      );
                    })}
                    {actions.length > 0 && (
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {actions.filter(action => !action.show || action.show(row)).map((action, index) => (
                              <DropdownMenuItem
                                key={index}
                                onClick={() => action.onClick(row)}
                                className={action.variant === 'destructive' ? 'text-destructive' : ''}
                              >
                                {action.icon}
                                <span className="ml-2">{action.label}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      )}

      {/* Footer info */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {sortedData.length} of {data.length} items
        </span>
      </div>
    </div>
  );
}
